import { Component, OnInit, OnDestroy } from '@angular/core';
import { BreakdownTechnicianStatus, BreakdownTechnicianStatusUpdate, DocumentCategory, UserLocation } from '../../models/user';
import { DriverService } from '../../shared/driver.service';
import { AuthService } from '../../auth.service';
import { AppModule } from '../../app.module';
import { NotificationService } from '../../shared/notification.service';
import { IncidentService } from '../../shared/incident.service';
import { Incident, Document, UploadingFile, IncidentOperator } from '../../models/incident';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TyreLocation, UserType } from '../../shared/enums';
import { WakelockService } from '../../shared/wakelock.service';
import { Subscription } from 'rxjs';
import { ImageQueueService, QueuedImage } from '../../shared/image-queue.service';

@Component({
  selector: 'app-bdtech-dash',
  imports: [AppModule],
  templateUrl: './bdtech-dash.html',
  styleUrl: './bdtech-dash.less'
})
export class BdtechDash implements OnInit, OnDestroy {

  public readonly DESCRIPTION_MAX_LENGTH = 30;
  public technicianStatus: BreakdownTechnicianStatus;
  public isLoading: boolean = true;
  public isSendingLocationUpdate: boolean = false;
  public incident: Incident | null = null;
  public mapUrl: SafeResourceUrl | null = null;
  private locationUpdateInterval: any = null;
  public showFullDescription: boolean = true;
  public showDriverDetails: boolean = false;
  public showOperatorDetails: boolean = false;
  public isViewImagesModalVisible: boolean = false;
  public isCompleteIncidentModalVisible: boolean = false;
  public isCompletingIncident: boolean = false;

  public pendingImages: QueuedImage[] = [];
  private queueInterval: any = null;
  private onlineHandler: (() => void) | null = null;
  private queueSubscriptions: Subscription[] = [];

  public imageCategories: DocumentCategory[] = [];


  constructor(private driverService: DriverService, 
      private authService: AuthService, 
      private notification: NotificationService, 
      private incidentService: IncidentService, 
      private sanitizer: DomSanitizer,
      private wakeLockService: WakelockService,
      public imageQueueService: ImageQueueService) { }

  ngOnInit() {
    this.loadTechnicianStatus();
    this.loadDocumentCategories();
    this.wakeLockService.requestWakeLock();
  }

  ngOnDestroy() {
    this.stopLocationUpdates();
    this.wakeLockService.releaseWakeLock();
    if (this.queueInterval){
      clearInterval(this.queueInterval);
      this.queueInterval = null;
    }
    if (this.onlineHandler) {
      window.removeEventListener('online', this.onlineHandler);
      this.onlineHandler = null;
    }
    this.queueSubscriptions.forEach(sub => sub.unsubscribe());
    this.queueSubscriptions = [];
  }

  public refreshData() {
    this.ngOnInit();
  }


  private loadTechnicianStatus() {
    this.isLoading = true;
    this.driverService.getTechnicianDriverStatus(this.authService.user!.userId).subscribe({
      next: (data) => {
        this.technicianStatus = data;
        if (this.technicianStatus.incidentId && this.technicianStatus.incidentId > 0) {
          this.loadIncidentDetails(this.technicianStatus.incidentId);
        } else {
          this.isLoading = false;
        }
      },
      error: (error) => {
        this.isLoading = false;
        this.notification.handleError(error, 'Failed to load technician status');
      }
    });
  }

  private loadDocumentCategories() {
    this.driverService.getDocumentCategories().subscribe({
      next: (data) => {
        this.imageCategories = data;
      },
      error: (error) => {
        this.notification.handleError(error, 'Failed to load document categories');
      }
    });
  }

  public getDriverStatus(): string {
    switch (this.technicianStatus.statusId) {
      case 1:
        return 'Available';
      case 2:
        return 'Unavailable';
      case 3:
        return 'Busy';
      default:
        return 'Unknown';
    }
  }

  public getServiceProviderOperator(): IncidentOperator | undefined { 
    if (this.incident) {
      return this.incident.incidentOperators.find(op => op.userType === UserType.callcenterOperator);
    }
    else {
      return undefined;
    }
  }

  public onChangeStatus(newStatusId: number, minutes: number = 0) {
    //We will get the lat and lon in a future version. Or maybe now?
    const driverStatus = new BreakdownTechnicianStatusUpdate();
    driverStatus.statusId = newStatusId;
    driverStatus.latitude = 0;
    driverStatus.longitude = 0;
    driverStatus.clientEta = 0;
    driverStatus.minutes = minutes;
    this.isLoading = true;
    this.driverService.updateTechnicianDriverStatus(this.authService.user!.userId, driverStatus).subscribe({
      next: (data) => {
        this.isLoading = false;
        this.technicianStatus = data;
        this.notification.showSuccess('Status updated successfully');
      },
      error: (error) => {
        this.isLoading = false;
        this.notification.handleError(error, 'Failed to update status');
      }
    });
  }

  private sendLocationUpdate(silent: boolean = false) {

    if (!navigator.geolocation) {
      if (!silent) {
        this.notification.showError('Geolocation is not supported by your browser');
      }
      return;
    }
    this.isSendingLocationUpdate = true;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location: UserLocation = new UserLocation();
        location.userId = this.authService.user!.userId;
        location.locationLatitude = position.coords.latitude;
        location.locationLongitude = position.coords.longitude;
        location.locationDescription = '';
        
        // Update local status with current position
        this.technicianStatus.latitude = position.coords.latitude;
        this.technicianStatus.longitude = position.coords.longitude;
        
        this.driverService.updateDriverLocation(location).subscribe({
          next: () => {
            this.isSendingLocationUpdate = false;
            if (!silent) {
              this.notification.showSuccess('Location updated successfully');
            }
          },
          error: (error) => {
            this.isSendingLocationUpdate = false;
            if (!silent) {
              this.notification.handleError(error, 'Failed to update location');
            }
          }
        });
      },
      (error) => {
        this.isSendingLocationUpdate = false;
        if (!silent) {
          let errorMessage = 'Failed to get current location';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location permission denied';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information unavailable';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out';
              break;
          }
          this.notification.showError(errorMessage);
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  }

  public onClickOpenGoogleMaps() {
    this.onSendLocationUpdate();
    const url = `https://www.google.com/maps/dir/?api=1&destination=${this.technicianStatus.incidentLatitude},${this.technicianStatus.incidentLongitude}`;

    window.open(url, '_blank');
  }

  public onSendLocationUpdate() {
    this.sendLocationUpdate();
  }

  private loadIncidentDetails(incidentId: number) {
    this.incidentService.getIncident(incidentId).subscribe({
      next: (data) => {
        this.incident = data;
        this.incidentService.DriverIncident = this.incident;
        this.mapUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
          `https://maps.google.com/maps?q=${this.technicianStatus.incidentLatitude},${this.technicianStatus.incidentLongitude}&z=15&output=embed`
        );
        this.isLoading = false;
        // Start periodic location updates every 5 minutes
        this.startLocationUpdates();
        this.initImageQueue(incidentId);
      },
      error: (error) => {
        this.isLoading = false;
        this.notification.handleError(error, 'Failed to load breakdown details');
      }
    });
  }

  private startLocationUpdates() {
    // Clear any existing interval first
    this.stopLocationUpdates();
    // Update location every 5 minutes (300,000 ms)
    this.locationUpdateInterval = setInterval(() => {
      this.sendLocationUpdate(true);
    }, 2 * 60 * 1000);
  }

  private stopLocationUpdates() {
    if (this.locationUpdateInterval) {
      clearInterval(this.locationUpdateInterval);
      this.locationUpdateInterval = null;
    }
  }

  public async onGallerySelect(event: Event, category?: DocumentCategory) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    if (!this.incident || !this.incident.incidentId) {
      this.notification.showError('Cannot upload images: No active breakdown');
      input.value = '';
      return;
    }

    //this.isLoading = true;
    const files = Array.from(input.files);
    // let uploadedCount = 0;
    // let failedCount = 0;
    // const maxSizeInBytes = 2 * 1024 * 1024; // 2MB

    for (const file of files) {
      const compressedBase64 = await this.compressImageHalf(file);
      const localId = await this.imageQueueService.addImage({
          incidentId: this.incident!.incidentId,
          fileBase64: compressedBase64,
          name: file.name,
          documentClassificationId: 1, // Proof of Work
          createdByUserId: this.authService.user!.userId,
          dtCreated: new Date(),
          description: category?.categoryDescription || '',
          subCategoryId: category?.categoryId
        });
      this.pendingImages.push({
            incidentId: this.incident!.incidentId,
            id: localId,
            fileBase64: compressedBase64,
            name: file.name,
            documentClassificationId: 1,
            createdByUserId: this.authService.user!.userId,
            dtCreated: new Date(),
            description: category?.categoryDescription || '',
            subCategoryId: category?.categoryId,
            status: 'pending',
            retryCount: 0
          });
    }
    this.imageQueueService.processQueue(this.incident.incidentId);
    input.value = '';
  }

  // private uploadGalleryPhoto(base64: string, fileName: string, uploadedCount: number, failedCount: number, totalFiles: number, input: HTMLInputElement) {
  //   const fileData: UploadingFile = {
  //     name: fileName,
  //     base64: base64,
  //     incidentId: this.incident!.incidentId,
  //     createdByUserId: this.authService.user!.userId,
  //     documentClassificationId: 1 // Proof of Work
  //   };
    
  //   this.incidentService.uploadFile(fileData, this.incident!.incidentId).subscribe({
  //     next: (document: Document) => {
  //       uploadedCount++;
  //       if (uploadedCount + failedCount === totalFiles) {
  //         this.isLoading = false;
  //         if (failedCount === 0) {
  //           this.notification.showSuccess(`${uploadedCount} image(s) uploaded successfully`);
  //         } else {
  //           this.notification.showWarning(`${uploadedCount} succeeded, ${failedCount} failed`);
  //         }
  //         input.value = '';
  //       }
  //     },
  //     error: (error) => {
  //       failedCount++;
  //       if (uploadedCount + failedCount === totalFiles) {
  //         this.isLoading = false;
  //         if (uploadedCount === 0) {
  //           this.notification.handleError(error, 'Failed to upload images');
  //         } else {
  //           this.notification.showWarning(`${uploadedCount} succeeded, ${failedCount} failed`);
  //         }
  //         input.value = '';
  //       }
  //     }
  //   });
  // }

  public async onCameraCapture(event: Event, category?: DocumentCategory) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      
      if (!this.incident || !this.incident.incidentId) {
        this.notification.showError('Cannot upload photo: No active breakdown');
        input.value = '';
        return;
      }

      const compressedBase64 = await this.compressImageHalf(file);
      const localId = await this.imageQueueService.addImage({
          incidentId: this.incident.incidentId,
          fileBase64: compressedBase64,
          name: file.name,
          documentClassificationId: 1, // Proof of Work
          createdByUserId: this.authService.user!.userId,
          dtCreated: new Date(),
          description: category?.categoryDescription || '',
          subCategoryId: category?.categoryId
        });
      this.pendingImages.push({
            id: localId,
            incidentId: this.incident!.incidentId,
            fileBase64: compressedBase64,
            name: file.name,
            documentClassificationId: 1,
            createdByUserId: this.authService.user!.userId,
            dtCreated: new Date(),
            description: category?.categoryDescription || '',
            subCategoryId: category?.categoryId,
            status: 'pending',
            retryCount: 0
          });
      this.imageQueueService.processQueue(this.incident.incidentId);
      input.value = '';
    }
  }

  // private uploadPhoto(base64: string, fileName: string, input: HTMLInputElement) {
  //   const fileData: UploadingFile = {
  //     name: fileName,
  //     base64: base64,
  //     incidentId: this.incident!.incidentId,
  //     createdByUserId: this.authService.user!.userId,
  //     documentClassificationId: 1 // Proof of Work
  //   };
    
  //   this.incidentService.uploadFile(fileData, this.incident!.incidentId).subscribe({
  //     next: (document: Document) => {
  //       this.isLoading = false;
  //       this.notification.showSuccess('Photo uploaded successfully');
  //       // Reset input so another photo can be taken
  //       input.value = '';
  //     },
  //     error: (error) => {
  //       this.isLoading = false;
  //       this.notification.handleError(error, 'Failed to upload photo');
  //       input.value = '';
  //     }
  //   });
  // }

  private compressImage(file: File, quality: number = 0.7, maxWidth: number = 1920): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e: any) => {
        const img = new Image();
        
        img.onload = () => {
          // Calculate new dimensions
          let width = img.width;
          let height = img.height;
          
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          
          // Create canvas and compress
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Failed to get canvas context'));
            return;
          }
          
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to base64 with compression
          const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
          resolve(compressedBase64);
        };
        
        img.onerror = () => {
          reject(new Error('Failed to load image'));
        };
        
        img.src = e.target.result;
      };
      
      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };
      
      reader.readAsDataURL(file);
    });
  }


  public getTyreLocationString(tyreLocation: number): string {
    switch (tyreLocation) {
      case TyreLocation.steer:
        return 'Steer';
      case TyreLocation.drive:
        return 'Drive';
      case TyreLocation.trailer:
        return 'Trailer';
      case TyreLocation.spare:
        return 'Spare';
      default:
        return 'Unknown';
    }
  }

  public toggleDescription(): void {
    this.showFullDescription = !this.showFullDescription;
  }

  public toggleDriverDetails(): void {
    this.showDriverDetails = !this.showDriverDetails;
  }

  public toggleOperatorDetails(): void {
    this.showOperatorDetails = !this.showOperatorDetails;
  }

  public getWhatsAppLink(): string {
    if (!this.incident?.driver?.phoneNumber) return '';
    // Remove any non-digit characters from phone number
    const cleanNumber = this.incident.driver.phoneNumber.replace(/\D/g, '');
    return `https://wa.me/${cleanNumber}`;
  }

  public getOperatorWhatsAppLink(): string {
    const operator = this.getServiceProviderOperator();
    if (!operator?.cellNumber) return '';
    // Remove any non-digit characters from phone number
    const cleanNumber = operator.cellNumber.replace(/\D/g, '');
    return `https://wa.me/${cleanNumber}`;
  }

  public getTruncatedDescription(): string {
    if (!this.incident?.incidentDescription) return '';
    if (this.incident.incidentDescription.length <= this.DESCRIPTION_MAX_LENGTH) {
      return this.incident.incidentDescription;
    }
    return this.incident.incidentDescription.substring(0, this.DESCRIPTION_MAX_LENGTH) + '...';
  }

  public hasExistingImages(): boolean {
    const hasUploaded = this.incident?.documents?.some(doc => doc.documentClassificationId === 1) ?? false;
    return hasUploaded || this.pendingImages.length > 0;
  }

  public getImageCount(): number {
    const uploaded = this.incident?.documents?.filter(doc => doc.documentClassificationId === 1).length ?? 0;
    return uploaded + this.pendingImages.length;
  }

  public hasPendingImages(): boolean {
    return this.pendingImages.length > 0;
  }

  public getIncidentImages(): Document[] {
    return this.incident?.documents?.filter(doc => doc.documentClassificationId === 1) ?? [];
  }

  public getCategoryImages(categoryId: number): Document[] {
    return this.getIncidentImages().filter(doc => doc.subCategoryId === categoryId) ?? [];
  }

  public getPendingImagesByCategory(categoryId: number): QueuedImage[] {
    return this.pendingImages.filter(img => img.documentClassificationId === 1 && img.subCategoryId === categoryId);
  }

  public onViewImages(): void {
    this.isViewImagesModalVisible = true;
  }

  public onCompleteIncident(): void {
    if (this.hasPendingImages()) {
      this.notification.showError('Cannot complete breakdown: images are still pending upload');
      return;
  }
  this.isCompleteIncidentModalVisible = true;
  }

  public onConfirmCompleteIncident(): void {
    if (!this.incident || !this.incident.incidentId) {
      this.notification.showError('No active breakdown to complete');
      return;
    }

    this.isCompletingIncident = true;
    this.incidentService.breakdownTechnicianCompleted(this.incident.incidentId).subscribe({
      next: () => {
        this.isCompletingIncident = false;
        this.isCompleteIncidentModalVisible = false;
        this.notification.showSuccess('Breakdown completed successfully');
        // Clear the incident
        this.incident = null;
        //this.incidentService.DriverIncident = null;
        this.mapUrl = null;
        this.stopLocationUpdates();
        // Reload technician status
        this.loadTechnicianStatus();
      },
      error: (error) => {
        this.isCompletingIncident = false;
        this.notification.handleError(error, 'Failed to complete breakdown');
      }
    });
  }

  private compressImageHalf(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e: any) => {
        const img = new Image();

        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(img.width / 2);
          canvas.height = Math.round(img.height / 2);
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            resolve(canvas.toDataURL('image/jpeg', 0.7));
          } else {
            reject(new Error('Failed to get canvas context'));
          }
        };

        img.onerror = () => {
          reject(new Error('Failed to load image'));
        };

        img.src = e.target.result;
      };

      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };

      reader.readAsDataURL(file);
    });
  }

  private initImageQueue(incidentId: number){
    this.imageQueueService.resetStuckUploads(incidentId)
    .then(() =>  this.imageQueueService.getPendingImagesForIncident(incidentId))
    .then((pending) => {
      this.pendingImages = pending;
      this.imageQueueService.processQueue(incidentId);
    });

    this.queueInterval = setInterval(() => {
      this.imageQueueService.processQueue(incidentId);
    }, 30 * 1000);

    this.onlineHandler = () => {
      this.imageQueueService.processQueue(incidentId);
    };
    window.addEventListener('online', this.onlineHandler);

    const sub = this.imageQueueService.uploadSuccess$.subscribe(({localId, document}) => {
      const pending = this.pendingImages.find(img => img.id === localId);
      if (pending) {
        document.localBase64 = pending.fileBase64;
      }
      this.incident?.documents.push(document);
      this.pendingImages = this.pendingImages.filter(img => img.id !== localId);
    });

    const errorSub = this.imageQueueService.uploadFailed$.subscribe(({ localId }) => {
      //this.notification.showError(`Failed to upload image with local ID ${localId}. It will be retried automatically.`);
      const img = this.pendingImages.find(i => i.id === localId);
      if (img) img.status = 'failed';
    }
    );
    this.queueSubscriptions.push(sub, errorSub);
  }

  public onRetryPendingImages(): void {
    if (!this.incident || !this.incident.incidentId) {
      this.notification.showError('No active breakdown to retry uploads for');
      return;
    }
    this.imageQueueService.processQueue(this.incident.incidentId);
  }
}
